<template>
  <div class="progress">
    <div
      ref="progressBarElement"
      class="progress-bar other"
      role="progressbar"
      aria-valuemin="0"
      aria-valuemax="100"></div>
  </div>
</template>

<script setup lang="ts">
  import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

  interface IFakeLoadingProgressBar {
    defaultProgress?: number;
    completionTrigger: boolean;
  }

  const props = withDefaults(defineProps<IFakeLoadingProgressBar>(), {
    defaultProgress: 80,
  });

  const emits = defineEmits(['progressBarCompleted']);

  const progress = ref(0);
  const progressBarElement = ref(null);

  function animateProgress(target: number, cb?: () => void) {
    if (!progressBarElement.value) return;
    if (progress.value < target) {
      progress.value += 0.5;
      if (progress.value > target) progress.value = target;
      progressBarElement.value.style.width = `${progress.value}%`;
      requestAnimationFrame(() => animateProgress(target, cb));
    } else if (cb) {
      progressBarElement.value.addEventListener('transitionend', cb);
    }
  }

  function onTransitionEnd() {
    if (progressBarElement.value) {
      progressBarElement.value.removeEventListener('transitionend', onTransitionEnd);
    }
    setTimeout(() => {
      emits('progressBarCompleted');
    }, 500);
  }

  onMounted(() => {
    animateProgress(props.defaultProgress);
  });

  onBeforeUnmount(() => {
    if (progressBarElement.value) {
      progressBarElement.value.removeEventListener('transitionend', onTransitionEnd);
    }
  });

  watch(
    () => props.completionTrigger,
    (newValue) => {
      if (!newValue) {
        animateProgress(100, onTransitionEnd);
      }
    },
  );
</script>

<style scoped lang="scss">
  .progress-bar {
    height: 2rem;
    width: 0;
    overflow: hidden;
    position: relative;
    background: linear-gradient(90deg, #fc466b 0%, #3f5efb 100%);
    background-size: 200% 100%;
    animation: progress-animation 7s linear infinite;
    transition: width 5s ease;
  }

  @keyframes progress-animation {
    0% {
      background-position: 0% 0%;
    }
    50% {
      background-position: 100% 0%;
    }
    100% {
      background-position: 0% 0%;
    }
  }
</style>
